import React, { useState, useEffect } from 'react';
import { useToast } from '@/contexts/ToastContext';
import { AISHA_API_BASE } from '@/constants';
import { 
  XMarkIcon,
  CalendarIcon
} from '../icons/HeroIcons';

interface InfoAddModalProps {
  isOpen: boolean;
  onClose: () => void;
  editingItem?: any;
}

const InfoAddModal: React.FC<InfoAddModalProps> = ({ isOpen, onClose, editingItem }) => {
  const { showToast } = useToast();
  const [date, setDate] = useState('');
  const [title, setTitle] = useState('');
  const [type, setType] = useState<'notice' | 'menu_add' | 'error'>('notice');
  const [content, setContent] = useState({
    message: '',
    category: '',
    menu_name: '',
    sample_input_url: '',
    sample_output_url: '',
    error_message: '',
    error_time: '',
    fix_time: ''
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (editingItem) {
      setDate(editingItem.date);
      setTitle(editingItem.title);
      setType(editingItem.type);
      setContent({
        message: editingItem.content.message || '',
        category: editingItem.content.category || '',
        menu_name: editingItem.content.menu_name || '',
        sample_input_url: editingItem.content.sample_input_url || '',
        sample_output_url: editingItem.content.sample_output_url || '',
        error_message: editingItem.content.error_message || '',
        error_time: editingItem.content.error_time || '',
        fix_time: editingItem.content.fix_time || ''
      });
    } else {
      const today = new Date().toISOString().split('T')[0];
      setDate(today);
    }
  }, [editingItem]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = {
        date,
        title,
        type,
        content: {}
      };

      if (type === 'notice') {
        payload.content = { message: content.message };
      } else if (type === 'menu_add') {
        payload.content = {
          message: content.message,
          category: content.category,
          menu_name: content.menu_name,
          sample_input_url: content.sample_input_url,
          sample_output_url: content.sample_output_url
        };
      } else if (type === 'error') {
        payload.content = {
          error_message: content.error_message,
          error_time: content.error_time,
          fix_time: content.fix_time
        };
      }

      if (editingItem) {
        const response = await fetch(`${AISHA_API_BASE}/admin/information/${editingItem.id}/update/`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify(payload)
        });
        const data = await response.json();
        if (data.success) {
          showToast('お知らせを更新しました', 'success');
          onClose();
        }
      } else {
        const response = await fetch(`${AISHA_API_BASE}/admin/information/create/`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify(payload)
        });
        const data = await response.json();
        if (data.success) {
          showToast('お知らせを作成しました', 'success');
          onClose();
        }
      }
    } catch (error) {
      console.error('Failed to save information:', error);
      showToast('保存に失敗しました', 'error');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-bold text-white">
            {editingItem ? 'お知らせ編集' : '新規お知らせ作成'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-200"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-300 mb-2">
              日付
            </label>
            <div className="relative">
              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="w-full px-3 py-2 pl-10 bg-gray-700 border border-gray-600 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
              <CalendarIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            </div>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-300 mb-2">
              タイトル
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent placeholder-gray-400"
              placeholder="お知らせのタイトルを入力"
              required
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-300 mb-2">
              種別
            </label>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setType('notice')}
                className={`px-4 py-2 rounded-lg transition-colors ${
                  type === 'notice'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                お知らせ
              </button>
              <button
                type="button"
                onClick={() => setType('menu_add')}
                className={`px-4 py-2 rounded-lg transition-colors ${
                  type === 'menu_add'
                    ? 'bg-green-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                メニュー追加
              </button>
              <button
                type="button"
                onClick={() => setType('error')}
                className={`px-4 py-2 rounded-lg transition-colors ${
                  type === 'error'
                    ? 'bg-red-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                エラー情報
              </button>
            </div>
          </div>

          {type === 'notice' && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-300 mb-2">
                メッセージ
              </label>
              <textarea
                value={content.message}
                onChange={(e) => setContent({ ...content, message: e.target.value })}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent placeholder-gray-400"
                rows={4}
                placeholder="お知らせの内容を入力"
                required
              />
            </div>
          )}

          {type === 'menu_add' && (
            <>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  メッセージ
                </label>
                <textarea
                  value={content.message}
                  onChange={(e) => setContent({ ...content, message: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent placeholder-gray-400"
                  rows={4}
                  placeholder="メニュー追加の説明を入力"
                  required
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  カテゴリ
                </label>
                <input
                  type="text"
                  value={content.category}
                  onChange={(e) => setContent({ ...content, category: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent placeholder-gray-400"
                  placeholder="例: 車種変更、背景効果"
                  required
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  メニュー名
                </label>
                <input
                  type="text"
                  value={content.menu_name}
                  onChange={(e) => setContent({ ...content, menu_name: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent placeholder-gray-400"
                  placeholder="例: レトロフィルター、夕日背景"
                  required
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  元画像URL
                </label>
                <input
                  type="url"
                  value={content.sample_input_url}
                  onChange={(e) => setContent({ ...content, sample_input_url: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent placeholder-gray-400"
                  placeholder="https://example.com/input.jpg"
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  生成後画像URL
                </label>
                <input
                  type="url"
                  value={content.sample_output_url}
                  onChange={(e) => setContent({ ...content, sample_output_url: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent placeholder-gray-400"
                  placeholder="https://example.com/output.jpg"
                />
              </div>
            </>
          )}

          {type === 'error' && (
            <>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  エラーメッセージ
                </label>
                <textarea
                  value={content.error_message}
                  onChange={(e) => setContent({ ...content, error_message: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent placeholder-gray-400"
                  rows={3}
                  placeholder="エラーの内容を入力"
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    発生日時
                  </label>
                  <input
                    type="datetime-local"
                    value={content.error_time}
                    onChange={(e) => setContent({ ...content, error_time: e.target.value })}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    修正日時
                  </label>
                  <input
                    type="datetime-local"
                    value={content.fix_time}
                    onChange={(e) => setContent({ ...content, fix_time: e.target.value })}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
            </>
          )}

          <div className="flex justify-end gap-2 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-300 bg-gray-700 rounded-lg hover:bg-gray-600 transition-colors"
            >
              キャンセル
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              {loading ? '保存中...' : editingItem ? '更新' : '作成'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default InfoAddModal;